/**
 * KanbanColumn - Coluna (fase) do Kanban de Conversas
 * 
 * Representa uma fase do kanban com header editável,
 * lista de cards, área de drop, paginação infinita e opção para adicionar conversas
 * 
 * v3.0.0 - Adicionado suporte a reordenação de colunas via useSortable (drag & drop)
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { MoreHorizontal, Pencil, Trash2, Check, X, Plus, UserPlus, Palette, Lock, Zap, Loader2, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { KanbanCard } from './KanbanCard';
import { AddConversationDialog } from './AddConversationDialog';
import { useKanbanConversationColumn, type KanbanConversationFilters, type KanbanConversationItem } from '@/hooks/useKanbanConversationColumn';
import { useKanbanConversationCrmData } from '@/hooks/useKanbanConversationCrmData';
import type { KanbanPhase, KanbanConversation } from '@/hooks/useKanban';
// v1.1.0: Import do supabase removido - tags agora vêm inline da RPC
import { createLogger } from '@/lib/logger';

// Logger para depuração
const logger = createLogger('KanbanColumn');

// Paleta de 20 cores organizadas em 4 linhas temáticas
const PHASE_COLORS = [
  // Linha 1 - Cores primárias vibrantes
  '#3b82f6', // Azul
  '#22c55e', // Verde
  '#ef4444', // Vermelho
  '#f97316', // Laranja
  '#eab308', // Amarelo
  
  // Linha 2 - Tons alternativos
  '#8b5cf6', // Roxo
  '#ec4899', // Rosa
  '#06b6d4', // Ciano
  '#14b8a6', // Teal/Turquesa
  '#84cc16', // Lima/Verde limão
  
  // Linha 3 - Tons pastel/suaves
  '#60a5fa', // Azul claro
  '#4ade80', // Verde claro
  '#f87171', // Vermelho claro
  '#fb923c', // Laranja claro
  '#facc15', // Amarelo claro
  
  // Linha 4 - Tons neutros e escuros
  '#a855f7', // Violeta
  '#f472b6', // Rosa claro
  '#6b7280', // Cinza
  '#374151', // Cinza escuro
  '#1f2937', // Grafite
];

/**
 * Converte cor hex para rgba com opacidade
 */
const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

interface KanbanColumnProps {
  phase: KanbanPhase;
  filters?: KanbanConversationFilters;
  conversationCount?: number; // Contador vindo do stats
  /** Exibe dados CRM (deal + contrato) nos cards */
  showCrmInfo?: boolean;
  onUpdatePhase: (id: string, updates: Partial<KanbanPhase>) => Promise<any>;
  onDeletePhase: (id: string) => Promise<boolean>;
  onConversationClick?: (conversation: KanbanConversation) => void;
  /** Callback ao clicar na seção CRM do card - abre modal de deal */
  onCrmClick?: (dealId: string) => void;
  onConversationsAdded?: () => void;
  // v2.1.0: Permissões granulares (menu 'kanban') para ações estruturais
  canEditPhase?: boolean;
  canDeletePhase?: boolean;
  // Props de automação (opcionais)
  hasAutomation?: boolean;
  automationCount?: number;
  onAutomationClick?: () => void;
}

export function KanbanColumn({
  phase,
  filters,
  conversationCount,
  showCrmInfo = false,
  onUpdatePhase,
  onDeletePhase,
  onConversationClick,
  onCrmClick,
  onConversationsAdded,
  // Permissões (default true para retrocompatibilidade)
  canEditPhase = true,
  canDeletePhase = true,
  // Props de automação
  hasAutomation,
  automationCount = 0,
  onAutomationClick,
}: KanbanColumnProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(phase.name);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  
  // v1.1.0: Tags agora vêm inline da RPC (contact_tags JSONB) - não precisa mais de estado separado
  
  // Ref para o elemento trigger do infinite scroll
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Hook de paginação infinita - carrega conversas internamente
  const {
    conversations: rawConversations,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    invalidateColumn,
  } = useKanbanConversationColumn(phase.id, filters);

  // =========================================================================
  // Gera string estável dos IDs de contato (evita loop infinito de requisições)
  // Usa useMemo para criar uma chave estável que só muda quando os IDs mudam
  // Isso evita que a referência nova do array dispare o useEffect continuamente
  // =========================================================================
  const contactIdsKey = useMemo(() => {
    return rawConversations
      .map(c => c.contact_id)
      .filter((id): id is string => id !== null)
      .sort()
      .join(',');
  }, [rawConversations]);

  // Array de contact_ids para uso no hook CRM (derivado da mesma key estável)
  const contactIdsArray = useMemo(() => {
    return contactIdsKey ? contactIdsKey.split(',') : [];
  }, [contactIdsKey]);

  // =========================================================================
  // Busca dados CRM em batch (deal + contrato) - ativado apenas quando showCrmInfo=true
  // =========================================================================
  const { crmDataMap } = useKanbanConversationCrmData(contactIdsArray, showCrmInfo);

  // v1.1.0: Tags agora vêm inline da RPC get_kanban_conversations_by_phase
  // Removido useEffect que fazia SELECT direto na tabela contact_tag_assignments (N+1)
  // O campo contact_tags JSONB é retornado junto com cada conversa pela RPC

  // =========================================================================
  // IntersectionObserver para infinite scroll automático
  // Dispara fetchNextPage quando o elemento trigger fica visível
  // =========================================================================
  useEffect(() => {
    // Só observa se há mais páginas e não está carregando
    if (!hasNextPage || isFetchingNextPage || isLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Quando o elemento trigger fica visível, carrega próxima página
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      },
      { threshold: 0.1, rootMargin: '100px' } // Antecipa o carregamento em 100px
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, isLoading, fetchNextPage]);

  // Converte formato RPC para formato esperado pelo KanbanCard
  const conversations: KanbanConversation[] = rawConversations.map(conv => ({
    id: conv.id,
    kanban_phase_id: conv.kanban_phase_id,
    contact_id: conv.contact_id,
    inbox_id: conv.inbox_id,
    status: conv.status,
    unread_count: conv.unread_count,
    last_message_at: conv.last_message_at,
    last_message_preview: conv.last_message_preview,
    created_at: conv.created_at,
    assigned_to: conv.assigned_to,
    department_id: conv.department_id,
    assigned_agent: conv.assigned_to ? {
      id: conv.assigned_to,
      full_name: conv.assigned_agent_name,
      avatar_url: conv.assigned_agent_avatar,
    } : null,
    contact: conv.contact_id ? {
      id: conv.contact_id,
      name: conv.contact_name,
      phone: conv.contact_phone,
      email: conv.contact_email,
      avatar_url: conv.contact_avatar_url,
      tags: conv.contact_tags || [],
    } : null,
    // v1.2.0: dados do grupo (preenchidos quando a conversa é de grupo)
    group: conv.group_id ? {
      id: conv.group_id,
      name: conv.group_name,
      avatar_url: conv.group_avatar_url,
    } : null,
    inbox: conv.inbox_id ? {
      id: conv.inbox_id,
      name: conv.inbox_name || '',
      // v1.3.0: canal/provider para o badge do canal sobre o avatar
      channel_type: conv.inbox_channel_type,
      provider_slug: conv.inbox_provider_slug,
    } : null,
  }));

  // Configuração do sortable para reordenação de colunas (drag handle)
  const {
    attributes,
    listeners,
    setNodeRef: setSortableRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `column-${phase.id}`,
    data: {
      type: 'column',
      phaseId: phase.id,
    },
  });

  // Configura área de drop para receber conversas
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: phase.id,
    data: {
      type: 'phase',
      phase,
    },
  });

  // Refs separados: sortable no container externo, droppable no conteúdo interno
  // CORREÇÃO v3.0.1: Combinar no mesmo nó causa conflito no dnd-kit

  // IDs das conversas para ordenação
  const conversationIds = conversations.map(c => c.id);

  // Estilo de transformação para drag & drop de colunas
  // v3.1.0: Aplica transição suave para colunas vizinhas deslizarem
  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 200ms ease',
  };

  // Salva edição do nome
  const handleSaveEdit = async () => {
    if (editName.trim() && editName !== phase.name) {
      await onUpdatePhase(phase.id, { name: editName.trim() });
    }
    setIsEditing(false);
  };

  // Cancela edição
  const handleCancelEdit = () => {
    setEditName(phase.name);
    setIsEditing(false);
  };

  // Confirma exclusão
  const handleConfirmDelete = async () => {
    await onDeletePhase(phase.id);
    setShowDeleteDialog(false);
  };

  // Altera cor da fase
  const handleColorChange = async (color: string) => {
    await onUpdatePhase(phase.id, { color });
    setShowColorPicker(false);
  };

  // Callback após adicionar conversas
  const handleConversationsAdded = () => {
    invalidateColumn();
    onConversationsAdded?.();
  };

  // Número a exibir no badge (prefere stats, fallback para local)
  const displayCount = conversationCount ?? conversations.length;

  return (
    <div
      ref={setSortableRef}
      style={style}
      className={cn(
        'flex flex-col w-72 h-full shrink-0 rounded-lg bg-muted/30',
        'border border-border/50 overflow-hidden',
        isOver && 'ring-2 ring-primary/50 bg-primary/5',
        // v3.1.0: Esconde placeholder original (DragOverlay mostra a cópia)
        isDragging && 'opacity-0'
      )}
    >
      {/* Header da Coluna - Com fundo colorido sutil e handle de arrasto */}
      <div 
        className="p-3 border-b border-border/50 rounded-t-lg group"
        style={{ backgroundColor: hexToRgba(phase.color || '#6b7280', 0.15) }}
      >
        <div className="flex items-center justify-between gap-2">
          {isEditing ? (
            <div className="flex items-center gap-1 flex-1">
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="h-7 text-sm"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveEdit();
                  if (e.key === 'Escape') handleCancelEdit();
                }}
              />
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={handleSaveEdit}>
                <Check className="h-3.5 w-3.5" />
              </Button>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={handleCancelEdit}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 min-w-0">
                {/* Handle de arrasto - aparece no hover do header */}
                <button
                  {...attributes}
                  {...listeners}
                  className={cn(
                    "p-0.5 rounded cursor-grab active:cursor-grabbing",
                    "opacity-0 group-hover:opacity-100 transition-opacity",
                    "hover:bg-muted/50 focus:outline-none focus:ring-1 focus:ring-primary/50"
                  )}
                  title="Arraste para reordenar"
                >
                  <GripVertical className="h-4 w-4 text-muted-foreground" />
                </button>
                {/* Indicador circular de cor */}
                <span 
                  className="w-3 h-3 rounded-full shrink-0"
                  style={{ backgroundColor: phase.color || '#6b7280' }}
                />
                <span 
                  className={cn(
                    'font-semibold text-sm truncate',
                    canEditPhase ? 'cursor-pointer' : 'cursor-default'
                  )}
                  onDoubleClick={() => { if (canEditPhase) setIsEditing(true); }}
                  title={canEditPhase ? 'Duplo clique para editar' : phase.name}
                >
                  {phase.name}
                </span>
                <span className="text-xs text-muted-foreground bg-background/50 px-1.5 py-0.5 rounded">
                  {displayCount}
                </span>
                
                {/* Indicador de automação - Exibe ícone ⚡ quando há automações */}
                {hasAutomation && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onAutomationClick?.();
                    }}
                    className="flex items-center gap-0.5 text-amber-500 hover:text-amber-600 transition-colors"
                    title={`${automationCount} automação(ões) configurada(s)`}
                  >
                    <Zap className="h-3.5 w-3.5" />
                    <span className="text-[10px]">{automationCount}</span>
                  </button>
                )}
              </div>
              
              <div className="flex items-center gap-1">
                {/* Botão rápido de adicionar conversa */}
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-7 w-7"
                  onClick={() => setShowAddDialog(true)}
                  title="Adicionar conversas"
                >
                  <Plus className="h-4 w-4" />
                </Button>

                {/* Dropdown só aparece se houver pelo menos uma ação disponível
                    (Adicionar Conversas é sempre disponível para quem vê o board). */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setShowAddDialog(true)}>
                      <UserPlus className="h-4 w-4 mr-2" />
                      Adicionar Conversas
                    </DropdownMenuItem>

                    {/* Ações de edição da fase — só com permissão */}
                    {canEditPhase && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setShowColorPicker(true)}>
                          <Palette className="h-4 w-4 mr-2" />
                          Alterar Cor
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setIsEditing(true)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Renomear
                        </DropdownMenuItem>
                      </>
                    )}

                    {/* Excluir — só com permissão e quando não for fase padrão */}
                    {canDeletePhase && !phase.is_default && (
                      <DropdownMenuItem
                        onClick={() => setShowDeleteDialog(true)}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Excluir
                      </DropdownMenuItem>
                    )}
                    {/* Indicador de fase protegida (só faz sentido quando o usuário
                        teria permissão para excluir, mas a fase é padrão) */}
                    {canDeletePhase && phase.is_default && (
                      <DropdownMenuItem disabled className="text-muted-foreground">
                        <Lock className="h-4 w-4 mr-2" />
                        Fase protegida
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Dialog para seletor de cores - Solução definitiva */}
                <Dialog open={showColorPicker} onOpenChange={setShowColorPicker}>
                  <DialogContent className="sm:max-w-[280px]">
                    <DialogHeader>
                      <DialogTitle>Selecionar Cor</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-5 gap-3 py-2">
                      {PHASE_COLORS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          className={cn(
                            'w-10 h-10 rounded-full border-2 transition-all flex items-center justify-center',
                            phase.color === color 
                              ? 'border-foreground scale-110 ring-2 ring-offset-2 ring-primary' 
                              : 'border-transparent hover:scale-110'
                          )}
                          style={{ backgroundColor: color }}
                          onClick={() => handleColorChange(color)}
                        >
                          {phase.color === color && (
                            <Check className="h-5 w-5 text-white drop-shadow-md" />
                          )}
                        </button>
                      ))}
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Lista de Cards com paginação infinita */}
      {/* Área droppable separada do sortable para evitar conflito de refs */}
      <ScrollArea className="flex-1 h-0 min-h-0 p-2">
        <div ref={setDroppableRef} className="min-h-[100px]">
        <SortableContext items={conversationIds} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {isLoading ? (
              // Skeleton de loading
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : conversations.length === 0 ? (
              <div 
                className="text-center py-8 text-muted-foreground text-sm cursor-pointer hover:text-foreground transition-colors"
                onClick={() => setShowAddDialog(true)}
              >
                <Plus className="h-6 w-6 mx-auto mb-2 opacity-50" />
                Clique para adicionar conversas
              </div>
            ) : (
              <>
                {conversations.map((conversation) => (
                  <KanbanCard
                    key={conversation.id}
                    conversation={conversation}
                    crmData={showCrmInfo && conversation.contact?.id ? crmDataMap.get(conversation.contact.id) || null : null}
                    onCrmClick={onCrmClick}
                    onClick={() => onConversationClick?.(conversation)}
                  />
                ))}
                
                {/* Elemento trigger para infinite scroll automático */}
                {hasNextPage && (
                  <div 
                    ref={loadMoreRef} 
                    className="flex items-center justify-center py-4"
                  >
                    {isFetchingNextPage && (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </SortableContext>
        </div>
      </ScrollArea>

      {/* Dialog de Confirmação de Exclusão */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir fase "{phase.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              As conversas desta fase serão desvinculadas, mas não serão excluídas.
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-destructive text-destructive-foreground">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog para Adicionar Conversas */}
      <AddConversationDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        phaseId={phase.id}
        phaseName={phase.name}
        onConversationsAdded={handleConversationsAdded}
      />
    </div>
  );
}
